export const STRAPI_URL = process.env.NEXT_PUBLIC_STRAPI_URL ?? "";

export type StrapiEntity<T> = {
  data: {
    id: string;
    attributes: T;
  };
};

export type StrapiCollection<T> = {
  data: {
    id: string;
    attributes: T;
  }[];
};

export type StrapiImage = {
  url: string;
  alternativeText: string | null;
};

export type Cta = {
  label: string;
  url: string;
};

export const formatStrapiCollection = <T>(
  strapiCollection: StrapiCollection<T>
): T[] => strapiCollection.data.map((entity) => entity.attributes);

export const formatStrapiEntity = <T>(strapiEntity: StrapiEntity<T>): T =>
  strapiEntity.data.attributes;

export const addBasePathToStrapiMedia = (
  image: StrapiImage | null | undefined
): string => {
  if (image == null || image.url === "") {
    return "";
  }

  return image.url.startsWith("/")
    ? `${process.env.NEXT_PUBLIC_STRAPI_URL ?? ""}${image.url}`
    : image.url;
};

const fetchStrapiData = async (path: string, query: string, locale = "fr") => {
  const url = `${process.env.NEXT_PUBLIC_STRAPI_URL ?? ""}/api/${path}`;
  const formattedQuery = query ? `&${query}` : "";

  return fetch(`${url}?locale=${locale}${formattedQuery}`).then((response) =>
    response.json()
  );
};

export const getStrapiEntity = <T>(
  path: string,
  query: string,
  locale = "fr"
): Promise<T> =>
  fetchStrapiData(path, query, locale).then((json: StrapiEntity<T>) => {
    const data = (json as { data: unknown | null | undefined }).data;
    if ((data === null || data === undefined) && locale !== "fr") {
      return getStrapiEntity<T>(path, query, "fr");
    }

    return formatStrapiEntity(json);
  });

export const getStrapiCollection = <T>(
  path: string,
  query: string,
  locale = "fr"
): Promise<T[]> =>
  fetchStrapiData(path, query, locale).then((json: StrapiCollection<T>) => {
    const data = (json as { data: unknown[] | null | undefined }).data;
    if (
      (data === null || data === undefined || data.length === 0) &&
      locale !== "fr"
    ) {
      return getStrapiCollection<T>(path, query, "fr");
    }

    return formatStrapiCollection(json);
  });
